import { memo, useMemo } from "react";
import { TrendingUp } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLeaderboard } from "../hooks/useLeaderboard";
import { useLanguage } from "../contexts/LanguageContext";

interface UserMetricsProps {
  variant?: "card" | "compact" | "inline";
  showLabel?: boolean;
  className?: string;
}

export const UserMetrics = memo(function UserMetrics({
  variant = "card",
  showLabel = true,
  className = "",
}: UserMetricsProps) {
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const { leaderboard } = useLeaderboard(1000);

  // Get rank from leaderboard, but get predictions directly from profile
  // This ensures we show predictions even when user has no correct predictions yet
  const userStats = useMemo(() => {
    if (!user || !profile) return null;

    // Find user's rank in leaderboard (may be null if no correct predictions yet)
    const userEntry = leaderboard.find((entry) => entry.user_id === user.id);

    return {
      rank: userEntry?.rank || null,
      totalPredictions: profile.total_predictions || 0,
      correctPredictions: profile.correct_predictions || 0,
      trend: userEntry?.trend || null,
    };
  }, [user, profile, leaderboard]);

  if (!profile) {
    return null;
  }

  if (variant === "compact") {
    return (
      <div className={`flex items-center gap-2 text-sm ${className}`}>
        <div className="flex items-center gap-1.5">
          <TrendingUp
            className={`w-4 h-4 ${
              userStats?.trend === "down"
                ? "text-red-500 rotate-180"
                : "text-primary"
            }`}
          />
          <span className="text-primary font-semibold">
            {userStats?.rank ? `#${userStats.rank}` : "--"}
          </span>
        </div>
        <span className="text-slate-500">•</span>
        <span
          className="text-slate-500"
          title={
            t("landing.stats.totalPredictionsTooltip") ||
            "Total predictions across all matches"
          }
        >
          {userStats?.totalPredictions || 0}{" "}
          {t("landing.stats.predictions") || "predictions"}
        </span>
      </div>
    );
  }

  if (variant === "inline") {
    const isWhiteTheme = className.includes("text-white");
    const labelClass = isWhiteTheme ? "text-white/80" : "text-slate-500";
    const valueClass = isWhiteTheme ? "text-white" : "text-primary";
    const dividerClass = isWhiteTheme ? "bg-white/20" : "bg-slate-200";

    return (
      <div className={`flex items-center w-full ${className}`}>
        <div className="flex-1 text-center">
          <div className={`text-xs ${labelClass} mb-1`}>
            {showLabel ? t("landing.stats.yourRank") : ""}
          </div>
          <div className={`${valueClass} text-lg font-semibold`}>
            {userStats?.rank ? `#${userStats.rank}` : "--"}
          </div>
        </div>
        <div className={`w-px h-8 ${dividerClass}`}></div>
        <div className="flex-1 text-center">
          <div className={`text-xs ${labelClass} mb-1`}>
            {showLabel ? t("landing.stats.predictions") || "Predictions" : ""}
          </div>
          <div className={`${valueClass} text-lg font-semibold`}>
            {userStats?.totalPredictions || 0}
          </div>
        </div>
      </div>
    );
  }

  // Default card variant
  return (
    <div
      className={`bg-white rounded-2xl p-5 hover-lift border border-slate-200 ${className}`}
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className="bg-gradient-to-br from-primary to-primary-dark p-2 rounded-xl">
          <TrendingUp
            className={`w-5 h-5 text-white ${
              userStats?.trend === "down" ? "rotate-180" : ""
            }`}
          />
        </div>
        {showLabel && (
          <span className="text-xs text-slate-600 font-medium uppercase tracking-wide">
            {t("landing.stats.yourRank")}
          </span>
        )}
      </div>
      <div className="text-primary text-2xl font-semibold">
        {userStats?.rank ? `#${userStats.rank}` : "--"}
      </div>
      <div className="mt-1 text-xs text-slate-400">
        {userStats?.totalPredictions || 0}{" "}
        {t("landing.stats.predictions") || "predictions"}
      </div>
    </div>
  );
});
